import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { Button, Dropdown, Form } from 'react-bootstrap';

import { lomavisCreatorRootState } from '../../../../../pages/lomavisCreator';
import { AudioTrack } from './trackList';
import { searchInstagramAudio } from '../../../../../redux/slices/lomavisCreator/lomavisCreator';

export type InstagramAudioType = 'music' | 'original_sound';
export type InstagramAudioTypeFilter = 'all' | InstagramAudioType;

interface Props {
  audioTypeFilter: InstagramAudioTypeFilter;
  onAudioTypeFilterChange: (type: InstagramAudioTypeFilter) => void;
  query: string;
  onQueryChange: (query: string) => void;
  onResults: (results: AudioTrack[]) => void;
  onLoadingChange: (loading: boolean) => void;
  onErrorChange: (code: string | null) => void;
  audioVolume: number;
  videoVolume: number;
  onAudioVolumeChange: (value: number) => void;
  onVideoVolumeChange: (value: number) => void;
  hasSelectedTrack: boolean;
  hasVideo: boolean;
}

const DEBOUNCE_DELAY = 400;
const AUDIO_TYPE_FILTER_OPTIONS: Array<{
  value: InstagramAudioTypeFilter;
  labelKey: string;
  defaultLabel: string;
}> = [
  {
    value: 'all',
    labelKey: 'lomavisCreator.IG.music.audioTypeAll',
    defaultLabel: 'All'
  },
  {
    value: 'music',
    labelKey: 'lomavisCreator.IG.music.tabMusic',
    defaultLabel: 'Music'
  },
  {
    value: 'original_sound',
    labelKey: 'lomavisCreator.IG.music.tabOriginalSound',
    defaultLabel: 'Original sound'
  }
];

export const MusicSearch: React.FC<Props> = ({
  audioTypeFilter,
  onAudioTypeFilterChange,
  query,
  onQueryChange,
  onResults,
  onLoadingChange,
  onErrorChange,
  audioVolume,
  videoVolume,
  onAudioVolumeChange,
  onVideoVolumeChange,
  hasSelectedTrack,
  hasVideo
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch<any>();
  const [showFilter, setShowFilter] = useState(false);
  const [showSoundOptions, setShowSoundOptions] = useState(false);
  const [draftAudioTypeFilter, setDraftAudioTypeFilter] =
    useState<InstagramAudioTypeFilter>(audioTypeFilter);
  const locationUuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const musicSearchUrl = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.lomavisCreatorConfig?.additional_data
        ?.instagram_music_search_url
  );

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const handleFilterToggle = (nextShow: boolean) => {
    setShowFilter(nextShow);
    if (nextShow) {
      setShowSoundOptions(false);
      setDraftAudioTypeFilter(audioTypeFilter);
    }
  };

  const handleSoundOptionsToggle = (nextShow: boolean) => {
    setShowSoundOptions(nextShow);
    if (nextShow) {
      setShowFilter(false);
    }
  };

  const handleCancelFilter = () => {
    setDraftAudioTypeFilter(audioTypeFilter);
    setShowFilter(false);
  };

  const handleApplyFilter = () => {
    onAudioTypeFilterChange(draftAudioTypeFilter);
    setShowFilter(false);
  };

  useEffect(() => {
    // We need either an explicit URL from the backend creator config (works in
    // both location and multipost contexts) or a locationUuid we can resolve
    // ourselves.
    if (!musicSearchUrl && !locationUuid) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();
    abortRef.current = controller;
    onLoadingChange(true);

    debounceRef.current = setTimeout(() => {
      const searchAudioType = (audioType: InstagramAudioType) =>
        dispatch(
          searchInstagramAudio({
            locationUuid,
            searchUrl: musicSearchUrl,
            query,
            audioType,
            signal: controller.signal
          })
        );

      const search =
        audioTypeFilter === 'all'
          ? Promise.all([
              searchAudioType('music'),
              searchAudioType('original_sound')
            ]).then(([musicResults, originalSoundResults]) => [
              ...(musicResults || []),
              ...(originalSoundResults || [])
            ])
          : searchAudioType(audioTypeFilter);

      search
        .then((results) => {
          onResults(results || []);
          onErrorChange(null);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          const code = error.response?.data?.code || 'unknown_error';
          onErrorChange(code);
          onResults([]);
        })
        .finally(() => {
          if (requestIdRef.current === requestId) {
            onLoadingChange(false);
          }
        });
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      controller.abort();
    };
  }, [
    locationUuid,
    musicSearchUrl,
    query,
    audioTypeFilter,
    dispatch,
    onResults,
    onLoadingChange,
    onErrorChange
  ]);

  return (
    <div className="d-flex flex-column gap-2">
      <style>{`.ig-music-toggle.dropdown-toggle::after { display: none !important; }`}</style>
      <div className="d-flex align-items-center gap-2">
        <input
          type="search"
          className="form-control"
          placeholder={t(
            'lomavisCreator.IG.music.searchPlaceholder',
            'Search'
          )}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />

        <Dropdown
          align="end"
          autoClose={false}
          show={showFilter}
          onToggle={handleFilterToggle}
        >
          <Dropdown.Toggle
            as={Button}
            bsPrefix="btn"
            variant={audioTypeFilter === 'all' ? 'light' : 'primary'}
            size="sm"
            className={`btn-sm border d-inline-flex align-items-center justify-content-center ig-music-toggle ${
              audioTypeFilter === 'all' ? 'btn-light' : 'btn-primary'
            }`}
            style={{ width: 38, height: 38, padding: 0 }}
            id="instagram-audio-type-filter"
            aria-label={t(
              'lomavisCreator.IG.music.audioTypeFilter',
              'Filter audio type'
            )}
          >
            <i className="fa-duotone fa-bars-filter"></i>
          </Dropdown.Toggle>

          <Dropdown.Menu className="p-3 shadow-sm" style={{ minWidth: 270 }}>
            <div className="mb-2 small fw-semibold text-dark">
              {t('lomavisCreator.IG.music.audioType', 'Audio type')}
            </div>
            <select
              className="form-select form-select-sm mb-3"
              value={draftAudioTypeFilter}
              onChange={(event) =>
                setDraftAudioTypeFilter(
                  event.target.value as InstagramAudioTypeFilter
                )
              }
            >
              {AUDIO_TYPE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey, option.defaultLabel)}
                </option>
              ))}
            </select>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant="light"
                size="sm"
                className="border flex-grow-1"
                onClick={handleCancelFilter}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="flex-grow-1"
                onClick={handleApplyFilter}
              >
                {t('Apply')}
              </Button>
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown
          align="end"
          autoClose={false}
          show={showSoundOptions}
          onToggle={handleSoundOptionsToggle}
        >
          <Dropdown.Toggle
            as={Button}
            bsPrefix="btn"
            variant={showSoundOptions ? 'primary' : 'light'}
            size="sm"
            className={`btn-sm border d-inline-flex align-items-center justify-content-center ig-music-toggle ${
              showSoundOptions ? 'btn-primary' : 'btn-light'
            }`}
            style={{ width: 38, height: 38, padding: 0 }}
            id="instagram-audio-sound-options"
            aria-label={t(
              'lomavisCreator.IG.music.soundOptions',
              'Sound options'
            )}
          >
            <i className="fa-duotone fa-sliders"></i>
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="p-3 shadow-sm"
            style={{ minWidth: 320, zIndex: 1060 }}
          >
            <div className="mb-2 d-flex align-items-center justify-content-between">
              <span className="small fw-semibold text-dark">
                {t('lomavisCreator.IG.music.soundOptions', 'Sound options')}
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="p-0 text-muted"
                onClick={() => setShowSoundOptions(false)}
                aria-label={t('lomavisCreator.close', 'Close')}
              >
                <i className="fa-solid fa-xmark"></i>
              </Button>
            </div>
            <Form.Group className="mb-3">
              <Form.Label className="small mb-1 d-flex justify-content-between">
                <span>
                  {t('lomavisCreator.IG.music.audioVolume', 'Audio volume')}
                </span>
                <span className="text-muted">{audioVolume}%</span>
              </Form.Label>
              <Form.Range
                min={0}
                max={100}
                value={audioVolume}
                onChange={(e) => onAudioVolumeChange(Number(e.target.value))}
                disabled={!hasSelectedTrack}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="small mb-1 d-flex justify-content-between">
                <span>
                  {t('lomavisCreator.IG.music.videoVolume', 'Video volume')}
                </span>
                <span className="text-muted">{videoVolume}%</span>
              </Form.Label>
              <Form.Range
                min={0}
                max={100}
                value={videoVolume}
                onChange={(e) => onVideoVolumeChange(Number(e.target.value))}
                disabled={!hasVideo}
              />
            </Form.Group>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="text-muted small mt-1">
        {query.trim().length === 0
          ? t('lomavisCreator.IG.music.trendingHeader', 'Trending audio')
          : t(
              'lomavisCreator.IG.music.searchResultsHeader',
              'Search results'
            )}
      </div>
    </div>
  );
};
